import { ChangeDetectionStrategy, Component, OnInit, TemplateRef, ViewChild, inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { take } from 'rxjs';
import { EjecutivoCementoUpsertCommand } from '../../../models/ejecutivo-cemento.model';
import { EjecutivoCementoVm } from '../../../models/ejecutivo-cemento.vm';
import { EjecutivosCementoStore } from '../../../stores/ejecutivos-cemento.store';
import { AdminServerTableCellContext, AdminServerTableColumn, AdminServerTablePaginationState } from '../../../shared/table/models/admin-server-table.model';
import { EjecutivosCementoUiEffectsService } from '../ui-effects/ejecutivos-cemento-ui-effects.service';
import { EjecutivoCementoServerErrors } from '../components/ejecutivo-cemento-detalle-modal/ejecutivo-cemento-detalle-modal.component';
import { buildEjecutivosCementoColumns } from './ejecutivos-cemento-table.columns';

const MODAL_CONFIRMACION = { centered: true, backdrop: 'static' as const, windowClass: 'cbb-admin-modal-stack' };

@Component({
  selector: 'app-ejecutivos-cemento-tabla',
  templateUrl: './ejecutivos-cemento-tabla.component.html',
  styleUrls: ['./ejecutivos-cemento-tabla.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EjecutivosCementoTablaComponent implements OnInit {
  @ViewChild('detalleTpl', { static: true }) detalleTpl!: TemplateRef<unknown>;
  @ViewChild('confirmGuardarTpl', { static: true }) confirmGuardarTpl!: TemplateRef<unknown>;
  @ViewChild('confirmDeactivateTpl', { static: true }) confirmDeactivateTpl!: TemplateRef<unknown>;
  @ViewChild('comunasCellTpl', { static: true }) comunasCellTpl!: TemplateRef<AdminServerTableCellContext<EjecutivoCementoVm>>;
  @ViewChild('actionsCellTpl', { static: true }) actionsCellTpl!: TemplateRef<AdminServerTableCellContext<EjecutivoCementoVm>>;

  readonly store = inject(EjecutivosCementoStore);
  private readonly modalService = inject(NgbModal);
  private readonly uiEffects = inject(EjecutivosCementoUiEffectsService);
  readonly tableColumns: AdminServerTableColumn<EjecutivoCementoVm>[] = [];
  selectedItem: EjecutivoCementoVm | null = null;
  saveFieldErrors: EjecutivoCementoServerErrors = {};
  saveErrorMessage = '';
  private pendingUpsert: EjecutivoCementoUpsertCommand | null = null;
  private detalleModalRef: NgbModalRef | null = null;
  private confirmModalRef: NgbModalRef | null = null;

  ngOnInit(): void {
    this.tableColumns.push(...buildEjecutivosCementoColumns({
      comunasCellTemplate: this.comunasCellTpl,
      actionsCellTemplate: this.actionsCellTpl,
    }));
    this.store.loadCatalog().subscribe({ error: () => this.uiEffects.showLoadError() });
    this.recargar();
  }

  get paginationState(): AdminServerTablePaginationState {
    return { page: this.store.page(), pageSize: this.store.pageSize(), total: this.store.totalItems(), totalPages: this.store.totalPages(), hasPreviousPage: this.store.hasPreviousPage(), hasNextPage: this.store.hasNextPage(), pageStartIndex: this.store.pageStartIndex(), pageEndIndex: this.store.pageEndIndex(), pageSizeOptions: [10, 20, 50, 100] };
  }
  get tituloConfirmarGuardar(): string { return this.esReactivacion ? 'Confirmar reactivación' : this.selectedItem ? 'Confirmar cambios' : 'Confirmar creación'; }
  get mensajeConfirmarGuardar(): string { return this.esReactivacion ? '¿Desea reactivar este ejecutivo?' : this.selectedItem ? '¿Desea guardar los cambios de este ejecutivo?' : '¿Desea crear este ejecutivo?'; }
  get etiquetaConfirmarGuardar(): string { return this.esReactivacion ? 'Reactivar' : this.selectedItem ? 'Guardar' : 'Crear'; }
  get esReactivacion(): boolean { return Boolean(this.selectedItem && !this.selectedItem.activo && this.pendingUpsert?.activo); }

  abrirCrear(): void { this.clearSaveErrors(); this.selectedItem = null; this.openDetalleModal(); }
  abrirEditar(item: EjecutivoCementoVm): void { this.clearSaveErrors(); this.selectedItem = item; this.openDetalleModal(); }
  prepararReactivar(item: EjecutivoCementoVm): void {
    this.selectedItem = item;
    this.pendingUpsert = { nombre: item.nombre, correo: item.correo, telefono: item.telefono, regionCode: item.regionCode, comunaIds: item.comunaIds, activo: true };
    this.confirmModalRef = this.modalService.open(this.confirmGuardarTpl, MODAL_CONFIRMACION);
  }
  prepararDesactivar(item: EjecutivoCementoVm): void { this.selectedItem = item; this.confirmModalRef = this.modalService.open(this.confirmDeactivateTpl, MODAL_CONFIRMACION); }
  guardar(command: EjecutivoCementoUpsertCommand): void {
    this.clearSaveErrors();
    this.pendingUpsert = command;
    this.confirmModalRef = this.modalService.open(this.confirmGuardarTpl, MODAL_CONFIRMACION);
  }

  confirmarGuardar(): void {
    const command = this.pendingUpsert;
    const selected = this.selectedItem;
    this.pendingUpsert = null;
    this.closeConfirm();
    if (!command) return;
    const request$ = selected ? this.store.updateItem(selected.id, command) : this.store.createItem(command);
    request$.subscribe({
      next: () => {
        this.cerrarDetalle();
        this.recargar(() => {
        if (!selected) this.uiEffects.showCreateSuccess();
        else if (!selected.activo && command.activo) this.uiEffects.showReactivateSuccess();
        else this.uiEffects.showUpdateSuccess();
        });
      },
      error: (error: unknown) => this.handleSaveError(error, Boolean(selected)),
    });
  }

  confirmarDesactivar(): void {
    const id = this.selectedItem?.id ?? 0;
    this.closeConfirm();
    if (!id) return;
    this.store.deactivateItem(id).subscribe({
      next: () => this.recargar(() => this.uiEffects.showDeactivateSuccess()),
      error: () => this.uiEffects.showDeactivateError(),
    });
  }

  cancelarConfirmacion(): void { this.pendingUpsert = null; this.closeConfirm(); }
  cerrarDetalle(): void {
    this.detalleModalRef?.dismiss();
    this.detalleModalRef = null;
    this.selectedItem = null;
    this.clearSaveErrors();
  }
  onSearchTermChange(value: string | number): void { this.store.setSearchTerm(String(value ?? '')); }
  onActiveFilterChange(value: 'all' | 'true' | 'false'): void { this.store.setActiveFilter(value); }
  onOrderingChange(value: string): void { this.store.setOrdering(value); }
  onPageChange(value: number): void { this.store.setPage(value); }
  onPageSizeChange(value: number): void { this.store.setPageSize(value); }
  comunasTexto(item: EjecutivoCementoVm): string { return item.comunas.length ? item.comunas.map((comuna) => comuna.nombre).join(', ') : 'Todas las comunas'; }
  readonly trackByItem = (_: number, item: EjecutivoCementoVm): string => item.trackId;

  private openDetalleModal(): void {
    const ref = this.modalService.open(this.detalleTpl, { centered: true, backdrop: 'static', size: 'lg' });
    this.detalleModalRef = ref;
    ref.hidden.pipe(take(1)).subscribe(() => { if (this.detalleModalRef === ref) this.detalleModalRef = null; this.selectedItem = null; });
  }
  private closeConfirm(): void { this.confirmModalRef?.dismiss(); this.confirmModalRef = null; }
  private clearSaveErrors(): void {
    this.saveFieldErrors = {};
    this.saveErrorMessage = '';
  }
  private handleSaveError(error: unknown, isUpdate: boolean): void {
    const response = error instanceof HttpErrorResponse ? error.error : null;
    if (error instanceof HttpErrorResponse && error.status === 400 && response && typeof response === 'object') {
      const fieldNames: (keyof EjecutivoCementoServerErrors)[] = ['nombre', 'correo', 'telefono', 'regionCode', 'comunaIds', 'activo'];
      const fieldErrors: EjecutivoCementoServerErrors = {};
      for (const fieldName of fieldNames) {
        const value = (response as Record<string, unknown>)[fieldName];
        if (Array.isArray(value) && value.length) fieldErrors[fieldName] = String(value[0]);
        else if (typeof value === 'string') fieldErrors[fieldName] = value;
      }
      this.saveFieldErrors = fieldErrors;
      const general = (response as Record<string, unknown>)['message']
        ?? (response as Record<string, unknown>)['detail']
        ?? (response as Record<string, unknown>)['non_field_errors'];
      this.saveErrorMessage = Array.isArray(general) ? String(general[0] ?? '') : String(general ?? 'Revise los campos marcados.');
      if (Object.keys(fieldErrors).length || this.saveErrorMessage) return;
    }
    const message = response && typeof response === 'object' && typeof (response as Record<string, unknown>)['message'] === 'string'
      ? (response as Record<string, unknown>)['message'] as string
      : 'No fue posible guardar el ejecutivo. Intente nuevamente.';
    this.saveErrorMessage = message;
    if (isUpdate) this.uiEffects.showUpdateError(message);
    else this.uiEffects.showCreateError(message);
  }
  private recargar(onSuccess?: () => void): void {
    this.store.clearError();
    this.store.loadItems().subscribe({ next: () => onSuccess?.(), error: () => this.uiEffects.showLoadError() });
  }
}
